import React from 'react';
import { Box, Divider, Link, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEnvelope,
    faFileAlt,
    faPen,
    faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons';
import { WorkItem } from '../api/workItems';
import { sourceKeyLabel } from '../utils/sourceKeyLabel';
import { SourceDocumentState } from './WorkItemDrawerHeader';
import {
    formatClassificationInfluencesSummary,
    getWorkItemCreatedFromLabel,
    getWorkItemSourceInfo,
    linkedEmailMessageId,
} from '../utils/workItemDisplay';

function semanticMergeCount(item: WorkItem): number {
    const meta = item.meta && typeof item.meta === 'object' ? (item.meta as Record<string, unknown>) : {};
    const merge = meta.semantic_merge;
    if (!merge || typeof merge !== 'object') return 0;
    const count = (merge as Record<string, unknown>).merged_count;
    if (typeof count === 'number' && Number.isFinite(count)) return count;
    if (typeof count === 'string' && count.trim()) {
        const parsed = Number(count);
        return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
}

export interface WorkItemLineageSectionProps {
    item: WorkItem;
    sourceDocumentState: SourceDocumentState;
    onOpenSourceDocument: () => void;
    motionTransition: string;
    onRevealSourceRule?: () => void;
    onUnmergeLast?: () => void;
    unmergeSaving?: boolean;
}

function sourceIcon(sourceType: 'email' | 'document' | 'manual' | 'unknown') {
    switch (sourceType) {
        case 'email':
            return faEnvelope;
        case 'document':
            return faFileAlt;
        case 'manual':
            return faPen;
        default:
            return faQuestionCircle;
    }
}

function readMetaString(item: WorkItem, key: string): string | null {
    const meta = item.meta && typeof item.meta === 'object' ? (item.meta as Record<string, unknown>) : {};
    const v = meta[key];
    return typeof v === 'string' && v.trim() ? v.trim() : null;
}

const documentStatusLine = (
    state: SourceDocumentState,
    hasDocumentId: boolean,
    snapshotOnly: boolean,
): {
    kind: 'link' | 'text' | 'none';
    text?: string;
} => {
    if (hasDocumentId) {
        if (state === 'checking') {
            return { kind: 'text', text: 'Checking source document…' };
        }
        if (state === 'available') {
            return { kind: 'link' };
        }
        if (state === 'deleted') {
            return { kind: 'text', text: 'Source document was removed' };
        }
        if (state === 'missing' || state === 'unknown_error') {
            return { kind: 'text', text: 'Source document unavailable' };
        }
        return { kind: 'none' };
    }
    if (snapshotOnly) {
        if (state === 'deleted') {
            return {
                kind: 'text',
                text: 'The source file is no longer linked because the document was removed.',
            };
        }
        return {
            kind: 'text',
            text: 'The source file is no longer linked to this task.',
        };
    }
    return { kind: 'none' };
};

export const WorkItemLineageSection: React.FC<WorkItemLineageSectionProps> = ({
    item,
    sourceDocumentState,
    onOpenSourceDocument,
    motionTransition,
    onRevealSourceRule,
    onUnmergeLast,
    unmergeSaving = false,
}) => {
    const info = getWorkItemSourceInfo(item);
    const createdFromLabel = getWorkItemCreatedFromLabel(item);
    const influenceSummary = formatClassificationInfluencesSummary(item.source_lineage);
    const icon = sourceIcon(info.sourceType);
    const hasDocumentId = Boolean(item.document_id);
    const hasSnapshot =
        Boolean(readMetaString(item, 'source_document_filename')) ||
        Boolean(readMetaString(item, 'extraction_supersedes_key'));
    const snapshotOnly = !hasDocumentId && hasSnapshot;
    const docMeta = documentStatusLine(sourceDocumentState, hasDocumentId, snapshotOnly);

    const filenameLine = readMetaString(item, 'source_document_filename');
    const jobLine = readMetaString(item, 'source_document_job_label');
    const showRuleLineage =
        item.attention_source === 'rule' && (item.attention === 'watch' || item.attention === 'ignore');
    const sourceRuleLabel = sourceKeyLabel(item.source_key || '');
    const showSemanticMergeLineage = semanticMergeCount(item) >= 1;

    let secondaryText: string | null = null;

    if (info.sourceType === 'email') {
        // A linked email needs no second line; never name a tab here (C4).
        secondaryText = linkedEmailMessageId(item)
            ? null
            : 'This item is not linked to a stored email message';
    } else if (info.sourceType === 'document') {
        if (filenameLine) {
            secondaryText = filenameLine;
        }
    }

    const influenceOverflow = influenceSummary.isEmpty
        ? 0
        : Math.max(0, influenceSummary.total - influenceSummary.lines.length);

    return (
        <Box
            data-testid="WB_LINEAGE_SECTION"
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 3,
                py: 0.75,
                borderBottom: '1px solid var(--color-border)',
                bgcolor: 'var(--color-bg-tertiary)',
                color: 'var(--color-text-secondary)',
                transition: `color ${motionTransition}, border-color ${motionTransition}`,
            }}
        >
            <FontAwesomeIcon icon={icon} className="text-[14px] shrink-0 opacity-90" aria-hidden />
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                    variant="caption"
                    component="div"
                    sx={{ fontWeight: 600, color: 'var(--color-text-secondary)', letterSpacing: 0.2 }}
                >
                    Created from
                </Typography>
                <Typography variant="caption" component="div" sx={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
                    {createdFromLabel}
                </Typography>
                {info.sourceType === 'email' && secondaryText && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'var(--color-text-secondary)', mt: 0.25 }}>
                        {secondaryText}
                    </Typography>
                )}
                {info.sourceType === 'document' && secondaryText && secondaryText !== createdFromLabel && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'var(--color-text-secondary)', mt: 0.25 }}>
                        {secondaryText}
                    </Typography>
                )}
                {info.sourceType === 'document' && jobLine && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'var(--color-text-secondary)', mt: 0.25 }}>
                        {jobLine}
                    </Typography>
                )}
                {info.sourceType === 'document' && hasDocumentId && (
                    <Box sx={{ mt: 0.25 }}>
                        {docMeta.kind === 'link' && (
                            <Link
                                component="button"
                                type="button"
                                variant="caption"
                                onClick={() => onOpenSourceDocument()}
                                sx={{
                                    cursor: 'pointer',
                                    color: 'var(--color-accent-text)',
                                    fontWeight: 600,
                                    textDecoration: 'underline',
                                    border: 'none',
                                    background: 'none',
                                    p: 0,
                                    font: 'inherit',
                                }}
                            >
                                View source document
                            </Link>
                        )}
                        {docMeta.kind === 'text' && docMeta.text && (
                            <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)' }}>
                                {docMeta.text}
                            </Typography>
                        )}
                    </Box>
                )}
                {info.sourceType === 'document' && snapshotOnly && docMeta.kind === 'text' && docMeta.text && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'var(--color-text-secondary)', mt: 0.25 }}>
                        {docMeta.text}
                    </Typography>
                )}

                <Divider sx={{ my: 0.75, borderColor: 'var(--color-border)' }} />
                <Typography
                    variant="caption"
                    component="div"
                    sx={{ fontWeight: 600, color: 'var(--color-text-secondary)', letterSpacing: 0.2 }}
                >
                    Classification influences
                </Typography>
                {influenceSummary.isEmpty ? (
                    <Typography variant="caption" sx={{ display: 'block', color: 'var(--color-text-secondary)', mt: 0.25 }}>
                        No separate classification influences recorded.
                    </Typography>
                ) : (
                    <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.25 }}>
                        {influenceSummary.lines.map((line, idx) => (
                            <Typography
                                key={`influence-${idx}`}
                                component="li"
                                variant="caption"
                                sx={{ color: 'var(--color-text-secondary)', display: 'list-item' }}
                            >
                                {line}
                            </Typography>
                        ))}
                    </Box>
                )}
                {!influenceSummary.isEmpty && influenceOverflow > 0 && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'var(--color-text-secondary)', mt: 0.25 }}>
                        And {influenceOverflow} more
                    </Typography>
                )}
                {showRuleLineage && (
                    <Typography
                        variant="caption"
                        component="div"
                        data-testid="WB_DRAWER_SOURCE_RULE_LINEAGE"
                        sx={{ display: 'block', color: 'var(--color-text-secondary)', mt: 0.75 }}
                    >
                        Stacked by your {sourceRuleLabel} rule —{' '}
                        <Link
                            component="button"
                            type="button"
                            variant="caption"
                            onClick={() => onRevealSourceRule?.()}
                            sx={{
                                cursor: 'pointer',
                                color: 'var(--color-accent-text)',
                                fontWeight: 700,
                                textDecoration: 'underline',
                                border: 'none',
                                background: 'none',
                                p: 0,
                                font: 'inherit',
                            }}
                        >
                            change
                        </Link>
                    </Typography>
                )}
                {showSemanticMergeLineage && (
                    <Typography
                        variant="caption"
                        component="div"
                        data-testid="WB_DRAWER_SEMANTIC_MERGE_LINEAGE"
                        sx={{ display: 'block', color: 'var(--color-text-secondary)', mt: 0.75 }}
                    >
                        Updated from related mail —{' '}
                        <Link
                            component="button"
                            type="button"
                            variant="caption"
                            disabled={unmergeSaving}
                            onClick={() => onUnmergeLast?.()}
                            data-testid="WB_DRAWER_SEMANTIC_UNMERGE"
                            sx={{
                                cursor: 'pointer',
                                color: 'var(--color-accent-text)',
                                fontWeight: 700,
                                textDecoration: 'underline',
                                border: 'none',
                                background: 'none',
                                p: 0,
                                font: 'inherit',
                            }}
                        >
                            undo last
                        </Link>
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default WorkItemLineageSection;
